'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Info, Eye, EyeOff, Download } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  parseDimensions,
  getBleedSettings,
  getBleedSettingsFromValue,
  getSafetyMargin,
  getProductShape,
  parseMillimeters,
  formatDimensions,
  type Dimensions,
  type BleedSettings,
} from '@/lib/utils/dimensions';
import { createCutContourHiddenOptionalContentConfig } from '@/lib/utils/pdf-cutline';
import { resolveCornerRadiusMm, usesCustomCornerRadius } from '@/lib/product-options/corner-radius';
import { editorPageSizeMm } from '@/lib/utils/supplier-bleed';
import { createProductionPdf } from '@/lib/editor/production-pdf';
import { originalProductionPdf } from '@/lib/editor/original-production-pdf';

import { pdfPreviewScale, mayExtractPreviewCutline, PDF_PREVIEW_MAX_BYTES } from '@/lib/utils/pdf-preview-policy';

interface DesignPreviewProps {
  imageUrl: string;
  productionFile?: { url: string; fileName: string };
  fileSize?: number;
  sizeValue: string;      // e.g., "30_x_20_cm"
  category?: string;       // e.g., "canvas"
  productName?: string;
  productSlug?: string;    // e.g., "boxes-fefco-0201-digital"
  canvasEdgeType?: string; // e.g., "white", "black", "stretched", "mirrored", "wrapped"
  defaultShowGuides?: boolean; // Default state for guides (false for edited designs)
  finishingOptions?: Record<string, string | number>;
  // Print.com levert configuratie-specifieke werkbestanden (snijlijnen, bleed,
  // vouwlijnen). Voor stanswerk is ontwerpen zonder dat sjabloon gokwerk.
  printcomTemplates?: boolean;
}

async function drawSvgOverCanvas(targetCanvas: HTMLCanvasElement, svg: string) {
  const context = targetCanvas.getContext('2d');
  if (!context) return;

  await new Promise<void>((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => {
      context.drawImage(image, 0, 0, targetCanvas.width, targetCanvas.height);
      resolve();
    };
    image.onerror = () => reject(new Error('SVG mask could not be loaded'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

function getDrillholeDiameterMm(value: string) {
  const match = value.match(/(\d+)_?mm/i);
  return match ? Number(match[1]) : 6;
}

function getDrillholeCount(value: string) {
  if (!value || value === 'none') return 0;
  if (value.includes('corner')) return 4;
  const words: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };
  const word = value.split('_')[0];
  if (words[word]) return words[word];
  const match = value.match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

type EdgeName = 'left' | 'right' | 'top' | 'bottom';

function getRingSpacingMm(value: string) {
  const match = value.match(/(?:eyelets?|ringen).*?(\d+)/i);
  return match ? Number(match[1]) * 10 : 300;
}

function finishHasRings(value: string) {
  // Gordijnringen hangen in de tunnel en D-ringen zitten aan een band: geen
  // gaten in het doek, dus ook geen ringmarkeringen in het ontwerp.
  if (/curtain|gordijn|d_ring|strap_d/i.test(value)) return false;
  return /eyelet|ring/i.test(value);
}

function finishHasCornerRingsOnly(value: string) {
  return /corner|hoek/i.test(value);
}

function getRingFinishes(options: Record<string, string | number>): Record<EdgeName, string> {
  const allAround = String(options.finish_all_around || '');
  if (allAround) {
    return { left: allAround, right: allAround, top: allAround, bottom: allAround };
  }

  // Alleen bij een expliciete keuze "alle zijden gelijk" nemen de andere zijden
  // de afwerking van links over. Heeft het product die keuze niet, dan telt per
  // zijde de eigen waarde: anders kregen alle vier de zijden ringen terwijl de
  // klant ze alleen boven bestelde.
  const equalFinish = options.all_sides_equal_finish === 'yes';
  const fallback = String(options.finish_left || options.finish_top || '');
  const side = (value: string | number | undefined) => String(equalFinish ? fallback : value ?? '');
  return {
    left: String(options.finish_left || fallback),
    right: side(options.finish_right),
    top: side(options.finish_top),
    bottom: side(options.finish_bottom),
  };
}

function getShapeClipPath(shape: 'rectangle' | 'circle' | 'hexagon' | 'heart' | 'star' | 'cloud') {
  if (shape === 'circle') return 'circle(50% at 50% 50%)';
  if (shape === 'hexagon') return 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';
  if (shape === 'heart') return 'path("M 50 95 C 12 68, 5 45, 18 25 C 29 9, 45 14, 50 30 C 55 14, 71 9, 82 25 C 95 45, 88 68, 50 95 Z")';
  if (shape === 'star') return 'polygon(50% 6%, 61% 35%, 92% 35%, 67% 53%, 77% 84%, 50% 65%, 23% 84%, 33% 53%, 8% 35%, 39% 35%)';
  if (shape === 'cloud') return 'path("M 20 76 C 8 76, 4 66, 7 57 C 5 43, 18 31, 31 34 C 36 20, 52 16, 61 28 C 79 25, 92 36, 90 51 C 96 58, 92 76, 77 76 Z")';
  return undefined;
}

function hexagonPoints(insetPercent = 0) {
  const left = insetPercent;
  const right = 100 - insetPercent;
  const top = insetPercent;
  const bottom = 100 - insetPercent;
  return [
    `${25 + insetPercent * 0.5},${top}`,
    `${75 - insetPercent * 0.5},${top}`,
    `${right},50`,
    `${75 - insetPercent * 0.5},${bottom}`,
    `${25 + insetPercent * 0.5},${bottom}`,
    `${left},50`,
  ].join(' ');
}

export function DesignPreview({
  imageUrl,
  productionFile,
  fileSize,
  sizeValue,
  category,
  productName,
  productSlug,
  canvasEdgeType = 'wrapped',
  defaultShowGuides = true,
  finishingOptions = {},
  printcomTemplates = false,
}: DesignPreviewProps) {
  const [showGuides, setShowGuides] = useState(defaultShowGuides);
  const [isFetchingWorkfiles, setIsFetchingWorkfiles] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [convertedImageUrl, setConvertedImageUrl] = useState<string>(imageUrl);
  const [isConverting, setIsConverting] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const [uploadedTemplate, setUploadedTemplate] = useState<string | null>(null);
  const [detectedCutlineTemplate, setDetectedCutlineTemplate] = useState<string | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [stretchedEdges, setStretchedEdges] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  }>({});
  const [mirroredEdges, setMirroredEdges] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  }>({});

  // Parse product dimensions
  const finalDimensions = parseDimensions(sizeValue);
  const productContext = `${category || ''} ${productSlug || ''} ${productName || ''}`;
  const bleed = getBleedSettingsFromValue(finishingOptions.__bleedMm ?? finishingOptions.bleed) || getBleedSettings(productContext);
  const dimensions = finalDimensions ? { ...finalDimensions, ...editorPageSizeMm(finalDimensions, bleed, Number(finishingOptions.__bleedOutside) === 1) } : null;
  const safetyMargin = parseMillimeters(finishingOptions.__safeMarginMm ?? finishingOptions.safeMargin) ?? getSafetyMargin(productContext);
  const productShape = getProductShape(sizeValue);
  const shapeClipPath = getShapeClipPath(productShape);
  const activeGuideTemplate = detectedCutlineTemplate || uploadedTemplate;

  // Preview limits never alter the original uploaded print file.
  useEffect(() => {
    let cancelled = false;
    let loadingTask: any;
    let renderTask: any;
    let canvas: HTMLCanvasElement | undefined;
    const controller = new AbortController();
    const cutlineTimer = setTimeout(() => controller.abort(), 10_000);
    let previewTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      cancelled = true;
      clearTimeout(cutlineTimer);
      if (previewTimer) clearTimeout(previewTimer);
      controller.abort();
      renderTask?.cancel();
      void loadingTask?.destroy()?.catch(() => undefined);
      if (canvas) { canvas.width = 0; canvas.height = 0; }
    };
    setPreviewUnavailable(false);
    setDetectedCutlineTemplate(null);
    const isPDF = /\.pdf(?:$|\?)/i.test(imageUrl);
    if (!isPDF || imageUrl.startsWith('data:')) {
      setConvertedImageUrl(imageUrl);
      setIsConverting(false);
      return stop;
    }
    setConvertedImageUrl('');
    if (fileSize && fileSize > PDF_PREVIEW_MAX_BYTES) {
      setPreviewUnavailable(true);
      setIsConverting(false);
      return stop;
    }
    setIsConverting(true);
    previewTimer = setTimeout(() => {
      if (!cancelled) { setIsConverting(false); setPreviewUnavailable(true); }
      stop();
    }, 30_000);
    const loadPdfJs = async (): Promise<any> => {
      if ((window as any).pdfjsLib) return (window as any).pdfjsLib;
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const timer = setTimeout(() => { script.remove(); reject(new Error('PDF preview loader timeout')); }, 15_000);
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => { clearTimeout(timer); resolve((window as any).pdfjsLib); };
        script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('PDF preview loader failed')); };
        document.head.appendChild(script);
      });
    };
    void (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        if (cancelled) return;
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        const absoluteUrl = new URL(imageUrl, window.location.origin).href;
        loadingTask = pdfjsLib.getDocument({ url: absoluteUrl, disableAutoFetch: true, disableStream: true });
        loadingTask.onProgress = ({ loaded, total }: { loaded: number; total: number }) => {
          if (loaded > PDF_PREVIEW_MAX_BYTES || total > PDF_PREVIEW_MAX_BYTES) void loadingTask.destroy();
        };
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        const natural = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: pdfPreviewScale(natural.width, natural.height) });
        canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        const hiddenConfig = await createCutContourHiddenOptionalContentConfig(pdf);
        if (cancelled) return;
        renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport,
          optionalContentConfigPromise: hiddenConfig ? Promise.resolve(hiddenConfig) : undefined });
        await renderTask.promise;
        if (cancelled) return;
        setConvertedImageUrl(canvas.toDataURL('image/png'));
        setIsConverting(false);
        // Optional contour extraction must not block the usable preview and
        // must not re-download large documents through multiple fallbacks.
        if (mayExtractPreviewCutline(fileSize)) {
          const cutline = await fetch('/api/ai/pdf-cutline', { method: 'POST', signal: controller.signal,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileUrl: absoluteUrl }) })
            .then(response => response.ok ? response.json() : null).catch(() => null);
          if (cancelled) return;
          if (cutline?.maskSvg) {
            await drawSvgOverCanvas(canvas, cutline.maskSvg);
            if (!cancelled) setConvertedImageUrl(canvas.toDataURL('image/png'));
          }
          if (!cancelled && cutline?.cutlineSvg) setDetectedCutlineTemplate(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(cutline.cutlineSvg)}`);
        }
      } catch {
        if (!cancelled) { setIsConverting(false); setPreviewUnavailable(true); }
      } finally {
        clearTimeout(cutlineTimer);
        if (previewTimer) clearTimeout(previewTimer);
        void loadingTask?.destroy()?.catch(() => undefined);
        if (canvas) { canvas.width = 0; canvas.height = 0; }
      }
    })();
    return stop;
  }, [imageUrl, fileSize]);

  // Load image to get its dimensions
  useEffect(() => {
    // Een lege src is geen "geen bron" voor een <img>: sommige browsers lezen
    // dat als een relatieve url en doen dan alsnog een verzoek naar de
    // HUIDIGE pagina. Zonder deze guard (nodig sinds de imageUrl-check na de
    // hooks staat) zou dat bij elke render zonder afbeelding gebeuren.
    if (isConverting || !convertedImageUrl) return;

    const img = new window.Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.src = convertedImageUrl;
  }, [convertedImageUrl, isConverting]);

  // Fetch and load uploaded template if available
  useEffect(() => {
    // De vroege `if (!imageUrl) return null` hierboven stond eerst VOOR deze
    // hook en verhinderde daarmee dat dit effect ooit draaide zonder
    // afbeelding. Nu de guard na alle hooks staat (Rules of Hooks), moet die
    // voorwaarde hier expliciet terugkomen - anders vuurt dit een
    // netwerkverzoek af bij elke render zonder afbeelding maar met een
    // geldige productSlug/sizeValue (bv. de configurator vóór upload).
    if (!imageUrl || !productSlug || !sizeValue) {
      setUploadedTemplate(null);
      return;
    }

    const loadTemplate = async () => {
      setIsLoadingTemplate(true);
      try {
        const response = await fetch(`/api/template/product/${productSlug}?size=${encodeURIComponent(sizeValue)}`);

        // Don't throw on 404 or other errors - just skip template
        if (!response.ok) {
          console.log('No template available for this product/size');
          setUploadedTemplate(null);
          setIsLoadingTemplate(false);
          return;
        }

        const data = await response.json();

        if (data.template && data.template.url) {
          console.log('Found uploaded template SVG:', data.template);
          // Template is now SVG, use directly
          setUploadedTemplate(data.template.url);
        } else {
          setUploadedTemplate(null);
        }
      } catch (error) {
        console.error('Failed to load template:', error);
        setUploadedTemplate(null);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    loadTemplate();
  }, [imageUrl, productSlug, sizeValue]);

  // Generate mirrored edges when edge type is 'mirrored'
  useEffect(() => {
    if (canvasEdgeType !== 'mirrored' || !convertedImageUrl || !dimensions) {
      if (Object.keys(mirroredEdges).length > 0) {
        setMirroredEdges({});
      }
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');
      if (!ctx) return;

      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const edges: any = {};

      // Calculate trim area dimensions
      const trimWidth = dimensions.width - bleed.left - bleed.right;
      const trimHeight = dimensions.height - bleed.top - bleed.bottom;

      // Calculate how the image is cropped with object-cover
      const imageAspect = img.width / img.height;
      const trimAspect = trimWidth / trimHeight;

      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;

      if (imageAspect > trimAspect) {
        sourceWidth = img.height * trimAspect;
        sourceX = (img.width - sourceWidth) / 2;
      } else {
        sourceHeight = img.width / trimAspect;
        sourceY = (img.height - sourceHeight) / 2;
      }

      // Calculate edge strip widths (in pixels of the source image)
      // Strip moet minstens zo groot zijn als de bleed zone, maar we nemen een beetje extra
      // om zeker te zijn dat het de hele zone vult zonder white space
      const bleedRatio = bleed.top / dimensions.height; // Percentage bleed
      const edgeStripWidth = Math.max(20, Math.ceil(sourceWidth * bleedRatio * 2)); // 2x bleed size
      const edgeStripHeight = Math.max(20, Math.ceil(sourceHeight * bleedRatio * 2)); // 2x bleed size

      // Top edge - mirror a horizontal strip from the top of the visible image
      const topCanvas = document.createElement('canvas');
      topCanvas.width = Math.floor(sourceWidth);
      topCanvas.height = edgeStripHeight;
      const topCtx = topCanvas.getContext('2d');
      if (topCtx) {
        topCtx.save();
        topCtx.scale(1, -1);
        topCtx.translate(0, -edgeStripHeight);
        topCtx.drawImage(
          tempCanvas,
          Math.floor(sourceX), Math.floor(sourceY),
          Math.floor(sourceWidth), edgeStripHeight,
          0, 0,
          topCanvas.width, topCanvas.height
        );
        topCtx.restore();
        edges.top = topCanvas.toDataURL();
      }

      // Bottom edge - mirror a horizontal strip from the bottom of the visible image
      const bottomCanvas = document.createElement('canvas');
      bottomCanvas.width = Math.floor(sourceWidth);
      bottomCanvas.height = edgeStripHeight;
      const bottomCtx = bottomCanvas.getContext('2d');
      if (bottomCtx) {
        bottomCtx.save();
        bottomCtx.scale(1, -1);
        bottomCtx.translate(0, -edgeStripHeight);
        bottomCtx.drawImage(
          tempCanvas,
          Math.floor(sourceX), Math.floor(sourceY + sourceHeight - edgeStripHeight),
          Math.floor(sourceWidth), edgeStripHeight,
          0, 0,
          bottomCanvas.width, bottomCanvas.height
        );
        bottomCtx.restore();
        edges.bottom = bottomCanvas.toDataURL();
      }

      // Left edge - mirror a vertical strip from the left of the visible image
      const leftCanvas = document.createElement('canvas');
      leftCanvas.width = edgeStripWidth;
      leftCanvas.height = Math.floor(sourceHeight);
      const leftCtx = leftCanvas.getContext('2d');
      if (leftCtx) {
        leftCtx.save();
        leftCtx.scale(-1, 1);
        leftCtx.translate(-edgeStripWidth, 0);
        leftCtx.drawImage(
          tempCanvas,
          Math.floor(sourceX), Math.floor(sourceY),
          edgeStripWidth, Math.floor(sourceHeight),
          0, 0,
          leftCanvas.width, leftCanvas.height
        );
        leftCtx.restore();
        edges.left = leftCanvas.toDataURL();
      }

      // Right edge - mirror a vertical strip from the right of the visible image
      const rightCanvas = document.createElement('canvas');
      rightCanvas.width = edgeStripWidth;
      rightCanvas.height = Math.floor(sourceHeight);
      const rightCtx = rightCanvas.getContext('2d');
      if (rightCtx) {
        rightCtx.save();
        rightCtx.scale(-1, 1);
        rightCtx.translate(-edgeStripWidth, 0);
        rightCtx.drawImage(
          tempCanvas,
          Math.floor(sourceX + sourceWidth - edgeStripWidth), Math.floor(sourceY),
          edgeStripWidth, Math.floor(sourceHeight),
          0, 0,
          rightCanvas.width, rightCanvas.height
        );
        rightCtx.restore();
        edges.right = rightCanvas.toDataURL();
      }

      setMirroredEdges(edges);
    };
    img.src = convertedImageUrl;
  }, [canvasEdgeType, convertedImageUrl, dimensions, bleed, mirroredEdges]);

  // Generate stretched edges when edge type is 'stretched'
  useEffect(() => {
    if (canvasEdgeType !== 'stretched' || !convertedImageUrl || !dimensions) {
      if (Object.keys(stretchedEdges).length > 0) {
        setStretchedEdges({});
      }
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');
      if (!ctx) return;

      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const edges: any = {};

      // Calculate trim area dimensions
      const trimWidth = dimensions.width - bleed.left - bleed.right;
      const trimHeight = dimensions.height - bleed.top - bleed.bottom;

      // Calculate how the image is cropped with object-cover
      const imageAspect = img.width / img.height;
      const trimAspect = trimWidth / trimHeight;

      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;

      if (imageAspect > trimAspect) {
        // Image is wider - crop left and right
        sourceWidth = img.height * trimAspect;
        sourceX = (img.width - sourceWidth) / 2;
      } else {
        // Image is taller - crop top and bottom
        sourceHeight = img.width / trimAspect;
        sourceY = (img.height - sourceHeight) / 2;
      }

      // Top edge - stretch first visible pixel row
      const topCanvas = document.createElement('canvas');
      topCanvas.width = Math.floor(sourceWidth);
      topCanvas.height = Math.max(1, Math.ceil(bleed.top * 2));
      const topCtx = topCanvas.getContext('2d');
      if (topCtx) {
        const firstRow = ctx.getImageData(Math.floor(sourceX), Math.floor(sourceY), Math.floor(sourceWidth), 1);
        for (let y = 0; y < topCanvas.height; y++) {
          topCtx.putImageData(firstRow, 0, y);
        }
        edges.top = topCanvas.toDataURL();
      }

      // Bottom edge - stretch last visible pixel row
      const bottomCanvas = document.createElement('canvas');
      bottomCanvas.width = Math.floor(sourceWidth);
      bottomCanvas.height = Math.max(1, Math.ceil(bleed.bottom * 2));
      const bottomCtx = bottomCanvas.getContext('2d');
      if (bottomCtx) {
        const lastRow = ctx.getImageData(Math.floor(sourceX), Math.floor(sourceY + sourceHeight - 1), Math.floor(sourceWidth), 1);
        for (let y = 0; y < bottomCanvas.height; y++) {
          bottomCtx.putImageData(lastRow, 0, y);
        }
        edges.bottom = bottomCanvas.toDataURL();
      }

      // Left edge - stretch first visible pixel column
      const leftCanvas = document.createElement('canvas');
      leftCanvas.width = Math.max(1, Math.ceil(bleed.left * 2));
      leftCanvas.height = Math.floor(sourceHeight);
      const leftCtx = leftCanvas.getContext('2d');
      if (leftCtx) {
        const firstCol = ctx.getImageData(Math.floor(sourceX), Math.floor(sourceY), 1, Math.floor(sourceHeight));
        for (let x = 0; x < leftCanvas.width; x++) {
          leftCtx.putImageData(firstCol, x, 0);
        }
        edges.left = leftCanvas.toDataURL();
      }

      // Right edge - stretch last visible pixel column
      const rightCanvas = document.createElement('canvas');
      rightCanvas.width = Math.max(1, Math.ceil(bleed.right * 2));
      rightCanvas.height = Math.floor(sourceHeight);
      const rightCtx = rightCanvas.getContext('2d');
      if (rightCtx) {
        const lastCol = ctx.getImageData(Math.floor(sourceX + sourceWidth - 1), Math.floor(sourceY), 1, Math.floor(sourceHeight));
        for (let x = 0; x < rightCanvas.width; x++) {
          rightCtx.putImageData(lastCol, x, 0);
        }
        edges.right = rightCanvas.toDataURL();
      }

      setStretchedEdges(edges);
    };
    img.src = convertedImageUrl;
  }, [canvasEdgeType, convertedImageUrl, dimensions, bleed, stretchedEdges]);

  // Deze checks moeten NA alle hooks hierboven staan (Rules of Hooks): stond
  // de imageUrl-check eerder in de functie, dan sloeg elke render zonder
  // imageUrl alle useEffect-aanroepen hieronder over. Een latere render mét
  // imageUrl riep ze dan alsnog aan - andere hook-volgorde, corrupte state.
  if (!imageUrl || imageUrl.length === 0) {
    return null;
  }

  if (!dimensions) {
    return (
      <Card className="p-4 bg-yellow-50 border-yellow-200">
        <p className="text-sm text-yellow-800">
          Kon afmetingen niet bepalen uit geselecteerd formaat
        </p>
      </Card>
    );
  }

  // Calculate aspect ratio for preview container
  const aspectRatio = dimensions.width / dimensions.height;
  const previewMaxHeight = 'min(70vh, 640px)';
  const previewMaxWidth = `min(100%, calc(${previewMaxHeight} * ${aspectRatio}))`;

  // Calculate guide positions as percentages
  const bleedPercentage = {
    top: (bleed.top / dimensions.height) * 100,
    right: (bleed.right / dimensions.width) * 100,
    bottom: (bleed.bottom / dimensions.height) * 100,
    left: (bleed.left / dimensions.width) * 100,
  };

  const safetyPercentage = {
    top: ((bleed.top + safetyMargin) / dimensions.height) * 100,
    right: ((bleed.right + safetyMargin) / dimensions.width) * 100,
    bottom: ((bleed.bottom + safetyMargin) / dimensions.height) * 100,
    left: ((bleed.left + safetyMargin) / dimensions.width) * 100,
  };
  const shapeGuideInset = Math.max(bleedPercentage.top, bleedPercentage.right, bleedPercentage.bottom, bleedPercentage.left);
  const shapeSafeInset = Math.max(safetyPercentage.top, safetyPercentage.right, safetyPercentage.bottom, safetyPercentage.left);

  // Fixed supplier radius, or the customer's own radius for the shape preset.
  const roundedCornerRadiusMm = resolveCornerRadiusMm(finishingOptions, { widthMm: dimensions.width, heightMm: dimensions.height });
  const trimBorderRadius = roundedCornerRadiusMm
    ? `${Math.min(18, Math.max(4, roundedCornerRadiusMm * 0.9))}px`
    : undefined;
  const drillholeValue = String(finishingOptions.drillholes || '');
  const drillholeCount = getDrillholeCount(drillholeValue);
  const drillholeDiameterMm = getDrillholeDiameterMm(drillholeValue);
  const drillholeOffsetMm = Math.max(15, roundedCornerRadiusMm + drillholeDiameterMm);
  const drillholeSize = {
    width: `${(drillholeDiameterMm / dimensions.width) * 100}%`,
    height: `${(drillholeDiameterMm / dimensions.height) * 100}%`,
  };
  const drillholePositions = (() => {
    if (!drillholeCount) return [];
    if (!drillholeValue.includes('corner')) return [];
    const insetX = ((bleed.left + drillholeOffsetMm) / dimensions.width) * 100;
    const insetY = ((bleed.top + drillholeOffsetMm) / dimensions.height) * 100;
    const right = 100 - insetX;
    const bottom = 100 - insetY;
    return [
      { left: `${insetX}%`, top: `${insetY}%` },
      { left: `${right}%`, top: `${insetY}%` },
      { left: `${insetX}%`, top: `${bottom}%` },
      { left: `${right}%`, top: `${bottom}%` },
    ];
  })();
  const ringDiameterMm = 10;
  const ringOffsetMm = 18;
  const ringSize = {
    width: `${(ringDiameterMm / dimensions.width) * 100}%`,
    height: `${(ringDiameterMm / dimensions.height) * 100}%`,
  };
  const ringPositions = (() => {
    const finishes = getRingFinishes(finishingOptions);
    const trimLeft = bleed.left;
    const trimTop = bleed.top;
    const trimWidth = dimensions.width - bleed.left - bleed.right;
    const trimHeight = dimensions.height - bleed.top - bleed.bottom;
    const positions = new Map<string, { left: string; top: string }>();

    const add = (xMm: number, yMm: number) => {
      const x = Math.max(0, Math.min(dimensions.width, xMm));
      const y = Math.max(0, Math.min(dimensions.height, yMm));
      const key = `${Math.round(x * 10)}-${Math.round(y * 10)}`;
      positions.set(key, { left: `${(x / dimensions.width) * 100}%`, top: `${(y / dimensions.height) * 100}%` });
    };

    const addCorners = () => {
      add(trimLeft + ringOffsetMm, trimTop + ringOffsetMm);
      add(trimLeft + trimWidth - ringOffsetMm, trimTop + ringOffsetMm);
      add(trimLeft + ringOffsetMm, trimTop + trimHeight - ringOffsetMm);
      add(trimLeft + trimWidth - ringOffsetMm, trimTop + trimHeight - ringOffsetMm);
    };

    const addSide = (edge: EdgeName, finish: string) => {
      if (!finishHasRings(finish)) return;
      if (finishHasCornerRingsOnly(finish)) {
        addCorners();
        return;
      }

      const spacing = getRingSpacingMm(finish);
      const horizontal = edge === 'top' || edge === 'bottom';
      const length = horizontal ? trimWidth : trimHeight;
      const count = Math.max(2, Math.floor((length - ringOffsetMm * 2) / spacing) + 2);

      for (let i = 0; i < count; i += 1) {
        const ratio = count === 1 ? 0 : i / (count - 1);
        if (horizontal) {
          add(trimLeft + ringOffsetMm + (length - ringOffsetMm * 2) * ratio, edge === 'top' ? trimTop + ringOffsetMm : trimTop + trimHeight - ringOffsetMm);
        } else {
          add(edge === 'left' ? trimLeft + ringOffsetMm : trimLeft + trimWidth - ringOffsetMm, trimTop + ringOffsetMm + (length - ringOffsetMm * 2) * ratio);
        }
      }
    };

    (Object.entries(finishes) as Array<[EdgeName, string]>).forEach(([edge, finish]) => addSide(edge, finish));
    return Array.from(positions.values());
  })();
  const foldGuides = (() => {
    const fold = String(finishingOptions.fold || '');
    const verticalByFold: Record<string, number[]> = {
      single_4: [50],
      wikkel_6: [33.333, 66.667],
      zigzag_6: [33.333, 66.667],
      luik_6: [33.333, 66.667],
      wikkel_8: [25, 50, 75],
      zigzag_8: [25, 50, 75],
      luik_8: [25, 50, 75],
      cross_8: [50],
      wikkel_10: [20, 40, 60, 80],
      zigzag_10: [20, 40, 60, 80],
    };
    const horizontalByFold: Record<string, number[]> = {
      singlehorizontal_4: [50],
      cross_8: [50],
    };
    const trimLeft = bleed.left;
    const trimTop = bleed.top;
    const trimWidth = dimensions.width - bleed.left - bleed.right;
    const trimHeight = dimensions.height - bleed.top - bleed.bottom;
    return {
      vertical: (verticalByFold[fold] || []).map((position) => ({
        left: `${((trimLeft + trimWidth * (position / 100)) / dimensions.width) * 100}%`,
        top: `${(trimTop / dimensions.height) * 100}%`,
        height: `${(trimHeight / dimensions.height) * 100}%`,
      })),
      horizontal: (horizontalByFold[fold] || []).map((position) => ({
        top: `${((trimTop + trimHeight * (position / 100)) / dimensions.height) * 100}%`,
        left: `${(trimLeft / dimensions.width) * 100}%`,
        width: `${(trimWidth / dimensions.width) * 100}%`,
      })),
    };
  })();
  const tunnelGuides = (() => {
    const normalizedSlug = String(productSlug || '').toLowerCase();
    const isTextilePoster = normalizedSlug === 'textielposter';
    const isWallcloth = normalizedSlug === 'wandkleed';
    const tunnelFlatMm = Number(finishingOptions.tunnelFlatMm || (isTextilePoster ? 40 : isWallcloth ? 60 : 0));
    if (!tunnelFlatMm || tunnelFlatMm <= 0) return [];

    const trimLeft = bleed.left;
    const trimTop = bleed.top;
    const trimWidth = dimensions.width - bleed.left - bleed.right;
    const trimHeight = dimensions.height - bleed.top - bleed.bottom;
    if (trimHeight <= tunnelFlatMm * 2) return [];

    const finishing = String(finishingOptions.finishing || '');
    const offsets = isWallcloth && finishing !== 'top_bottom_tunnel'
      ? [tunnelFlatMm]
      : [tunnelFlatMm, trimHeight - tunnelFlatMm];

    return offsets.map((offset) => ({
      top: `${((trimTop + offset) / dimensions.height) * 100}%`,
      left: `${(trimLeft / dimensions.width) * 100}%`,
      width: `${(trimWidth / dimensions.width) * 100}%`,
    }));
  })();
  // Download print-ready file
  const handleDownloadPrintFile = async () => {
    toast.info('Printbestand wordt gegenereerd...');

    try {
      // A preview is never a replacement for the original production PDF.
      // Preserve its page boxes, vector artwork and technical cut separation.
      if (productionFile && /\.pdf(?:$|[?#])/i.test(productionFile.fileName)) {
        const blob = await originalProductionPdf(productionFile.url);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = productionFile.fileName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success('Origineel drukbestand gedownload');
        return;
      }
      const exportDimensions = editorPageSizeMm(dimensions, bleed, false);
      // Create a high-resolution canvas for print
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Use lower DPI for very large prints to avoid memory issues
      // Canvas products are typically 200x100cm or larger
      const isLargePrint = exportDimensions.width > 500 || exportDimensions.height > 500; // larger than 50cm
      const DPI = isLargePrint ? (150 / 25.4) : (300 / 25.4); // 150 DPI for large, 300 for small

      const canvasWidth = Math.floor(exportDimensions.width * DPI);
      const canvasHeight = Math.floor(exportDimensions.height * DPI);

      console.log('Creating canvas:', {
        dimensions: `${exportDimensions.width}x${exportDimensions.height}mm`,
        DPI: isLargePrint ? 150 : 300,
        canvasSize: `${canvasWidth}x${canvasHeight}px`,
        megapixels: (canvasWidth * canvasHeight / 1000000).toFixed(2)
      });

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Fill background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Load the image
      const img = new window.Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = () => {
          console.log('Image loaded:', {
            src: convertedImageUrl.substring(0, 50) + '...',
            width: img.width,
            height: img.height
          });
          resolve(img);
        };
        img.onerror = (e) => {
          console.error('Image load error:', e);
          reject(new Error('Failed to load image'));
        };
        img.src = convertedImageUrl;
      });

      if (!img.width || !img.height) {
        throw new Error('Image has no dimensions');
      }

      // Calculate trim area
      const trimWidth = exportDimensions.width - bleed.left - bleed.right;
      const trimHeight = exportDimensions.height - bleed.top - bleed.bottom;

      // Draw image to fill trim area (object-cover style)
      const imageAspect = img.width / img.height;
      const trimAspect = trimWidth / trimHeight;

      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;

      if (imageAspect > trimAspect) {
        sourceWidth = img.height * trimAspect;
        sourceX = (img.width - sourceWidth) / 2;
      } else {
        sourceHeight = img.width / trimAspect;
        sourceY = (img.height - sourceHeight) / 2;
      }

      const trimX = bleed.left * DPI;
      const trimY = bleed.top * DPI;
      const trimW = trimWidth * DPI;
      const trimH = trimHeight * DPI;

      console.log('Drawing image to canvas:', {
        source: { x: sourceX, y: sourceY, w: sourceWidth, h: sourceHeight },
        dest: { x: trimX, y: trimY, w: trimW, h: trimH }
      });

      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        trimX, trimY, trimW, trimH
      );

      console.log('Image drawn successfully');

      // Apply edge types if canvas product
      if (category?.toLowerCase().includes('canvas') && canvasEdgeType !== 'wrapped') {
        if (canvasEdgeType === 'white') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, trimY); // Top
          ctx.fillRect(0, canvas.height - bleed.bottom * DPI, canvas.width, bleed.bottom * DPI); // Bottom
          ctx.fillRect(0, 0, bleed.left * DPI, canvas.height); // Left
          ctx.fillRect(canvas.width - bleed.right * DPI, 0, bleed.right * DPI, canvas.height); // Right
        } else if (canvasEdgeType === 'black') {
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, canvas.width, trimY);
          ctx.fillRect(0, canvas.height - bleed.bottom * DPI, canvas.width, bleed.bottom * DPI);
          ctx.fillRect(0, 0, bleed.left * DPI, canvas.height);
          ctx.fillRect(canvas.width - bleed.right * DPI, 0, bleed.right * DPI, canvas.height);
        } else if (canvasEdgeType === 'stretched') {
          // Stretched edges - stretch first/last pixel rows/columns of visible image

          console.log('Applying stretched edges:', { trimX, trimY, trimW, trimH, canvasWidth: canvas.width, canvasHeight: canvas.height });

          // First do left and right edges (full height)
          // Left edge - stretch first pixel column
          const leftEdgeData = ctx.getImageData(trimX, trimY, 1, trimH);
          for (let x = 0; x < trimX; x++) {
            ctx.putImageData(leftEdgeData, x, trimY);
          }

          // Right edge - stretch last pixel column
          const rightEdgeData = ctx.getImageData(trimX + trimW - 1, trimY, 1, trimH);
          for (let x = trimX + trimW; x < canvas.width; x++) {
            ctx.putImageData(rightEdgeData, x, trimY);
          }

          // Then do top and bottom edges (full width including corners)
          // Top edge - stretch first pixel row (full width)
          const topEdgeData = ctx.getImageData(0, trimY, canvas.width, 1);
          for (let y = 0; y < trimY; y++) {
            ctx.putImageData(topEdgeData, 0, y);
          }

          // Bottom edge - stretch last pixel row (full width)
          const bottomEdgeData = ctx.getImageData(0, trimY + trimH - 1, canvas.width, 1);
          for (let y = trimY + trimH; y < canvas.height; y++) {
            ctx.putImageData(bottomEdgeData, 0, y);
          }
        } else if (canvasEdgeType === 'mirrored') {
          // Mirrored edges - mirror edge strips from the visible trim area

          // Create temporary canvas with current state to avoid drawing on itself
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          const tempCtx = tempCanvas.getContext('2d');
          if (!tempCtx) return;

          // Copy current canvas state to temp canvas
          tempCtx.drawImage(canvas, 0, 0);

          // Calculate edge strip sizes based on bleed ratio (same as preview)
          const bleedRatioH = bleed.top / exportDimensions.height;
          const bleedRatioW = bleed.left / exportDimensions.width;
          const edgeStripH = Math.max(20 * DPI, Math.ceil(trimH * bleedRatioH * 2));
          const edgeStripW = Math.max(20 * DPI, Math.ceil(trimW * bleedRatioW * 2));

          console.log('Mirrored edge generation:', {
            bleedTop: bleed.top * DPI,
            bleedLeft: bleed.left * DPI,
            edgeStripH,
            edgeStripW,
            trimX,
            trimY,
            trimW,
            trimH
          });

          // Top edge - mirror strip from top of trim area, fill bleed zone
          ctx.save();
          ctx.translate(trimX, 0);
          ctx.scale(1, -1);
          ctx.drawImage(
            tempCanvas,
            trimX, trimY, trimW, edgeStripH,
            0, -trimY, trimW, trimY
          );
          ctx.restore();

          // Bottom edge - mirror strip from bottom of trim area, fill bleed zone
          ctx.save();
          ctx.translate(trimX, trimY + trimH);
          ctx.scale(1, -1);
          ctx.drawImage(
            tempCanvas,
            trimX, trimY + trimH - edgeStripH, trimW, edgeStripH,
            0, -(canvas.height - trimY - trimH), trimW, canvas.height - trimY - trimH
          );
          ctx.restore();

          // Left edge - mirror strip from left of trim area, fill bleed zone
          ctx.save();
          ctx.translate(0, trimY);
          ctx.scale(-1, 1);
          ctx.drawImage(
            tempCanvas,
            trimX, trimY, edgeStripW, trimH,
            -trimX, 0, trimX, trimH
          );
          ctx.restore();

          // Right edge - mirror strip from right of trim area, fill bleed zone
          ctx.save();
          ctx.translate(trimX + trimW, trimY);
          ctx.scale(-1, 1);
          ctx.drawImage(
            tempCanvas,
            trimX + trimW - edgeStripW, trimY, edgeStripW, trimH,
            0, 0, canvas.width - trimX - trimW, trimH
          );
          ctx.restore();
        }
      }

      // Preview templates and safety guides are never printed.
      // The print shop will handle CMYK conversion with proper ICC profiles
      if (!isLargePrint) {
        console.log('Processing color profile (skipped for large prints)...');
        // For smaller prints we could do color correction here
        // But for now we skip it to avoid memory issues
      }

      const dataURL = canvas.toDataURL('image/png');
      if (!dataURL || dataURL === 'data:,') throw new Error('Canvas is empty');
      const file = await createProductionPdf({ dataURL, pixelWidth: canvas.width, pixelHeight: canvas.height,
        dpi: isLargePrint ? 150 : 300 }, { width: exportDimensions.width, height: exportDimensions.height,
        bleed, productName: productName || 'Design', sizeValue,
        cutSeparationName: String(finishingOptions.__cutContourName || 'CutContour'),
        cornerRadiusMm: usesCustomCornerRadius(finishingOptions) ? roundedCornerRadiusMm : undefined });
      const link = document.createElement('a');
      link.href = file.dataURL;
      link.download = file.fileName;
      link.click();

      toast.success('Print-ready PDF gedownload!', {
        description: `${isLargePrint ? '150' : '300'} DPI, met bleed, klaar voor professionele CMYK conversie bij drukker.`
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download mislukt', {
        description: error instanceof Error ? error.message : 'Onbekende fout opgetreden'
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview Container */}
      <Card className="p-4">
        <div className="mb-4 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-none">
            <h4 className="text-sm font-semibold text-gray-900">Preview</h4>
            <p className="break-words text-xs text-gray-600">
              {formatDimensions(finalDimensions || dimensions)}
              {productName && ` • ${productName}`}
            </p>
          </div>
          {/* flex-wrap: de knoppen breken naar een tweede regel in plaats van uit de kaart te lopen */}
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {printcomTemplates && productSlug && (
              <Button
                variant="outline"
                size="sm"
                disabled={isFetchingWorkfiles}
                onClick={async () => {
                  setIsFetchingWorkfiles(true);
                  try {
                    const response = await fetch('/api/printcom/workfile', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sku: productSlug }),
                    });
                    const data = await response.json();
                    const workfiles: string[] = Array.isArray(data?.workfiles) ? data.workfiles : [];
                    if (workfiles.length === 0) {
                      toast.info('Geen los sjabloon voor dit product', {
                        description: 'De hulplijnen in de preview tonen bleed en veilige marge.',
                      });
                    } else {
                      workfiles.forEach((url) => window.open(url, '_blank', 'noopener'));
                    }
                  } catch {
                    toast.error('Sjabloon ophalen mislukt, probeer het opnieuw.');
                  } finally {
                    setIsFetchingWorkfiles(false);
                  }
                }}
                className="w-full whitespace-normal sm:w-auto"
              >
                <Download className="h-4 w-4 mr-2" />
                {isFetchingWorkfiles ? 'Sjabloon ophalen...' : 'Sjabloon (snijlijnen)'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPrintFile}
              className="w-full whitespace-normal sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              Download printbestand
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGuides(!showGuides)}
              className="w-full whitespace-normal sm:w-auto"
            >
              {showGuides ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Verberg hulplijnen
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Toon hulplijnen
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Preview with guides */}
        <div
          className="relative mx-auto border border-gray-300 bg-white overflow-hidden"
          style={{
            aspectRatio: aspectRatio.toString(),
            width: previewMaxWidth,
            maxWidth: '100%',
            maxHeight: previewMaxHeight,
          }}
        >
          {/* Uploaded Image */}
          {previewUnavailable ? (
            <div className="flex h-full min-h-40 items-center justify-center p-4 text-center text-sm text-gray-600">
              De voorvertoning is niet beschikbaar. Je originele drukbestand blijft behouden.
            </div>
          ) : isConverting ? (
            // Show loading while converting PDF
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <p className="text-sm text-gray-600">PDF wordt geladen...</p>
            </div>
          ) : (() => {
            // Determine if image should fit only within trim area (red line)
            const shouldFitToTrimArea = category?.toLowerCase().includes('canvas') &&
                                       canvasEdgeType &&
                                       canvasEdgeType !== 'wrapped';

            if (shouldFitToTrimArea) {
              // Image fills only the trim area (inside red line), edges go in bleed zone
              return convertedImageUrl.startsWith('data:') ? (
                <div
                  className="absolute"
                  style={{
                    top: `${bleedPercentage.top}%`,
                    right: `${bleedPercentage.right}%`,
                    bottom: `${bleedPercentage.bottom}%`,
                    left: `${bleedPercentage.left}%`,
                    zIndex: 2,
                  }}
                >
                  <img
                    src={convertedImageUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    style={shapeClipPath ? { clipPath: shapeClipPath } : undefined}
                  />
                </div>
              ) : (
                <div
                  className="absolute"
                  style={{
                    top: `${bleedPercentage.top}%`,
                    right: `${bleedPercentage.right}%`,
                    bottom: `${bleedPercentage.bottom}%`,
                    left: `${bleedPercentage.left}%`,
                    zIndex: 2,
                  }}
                >
                  <Image
                    src={convertedImageUrl}
                    alt="Preview"
                    fill
                    className="object-cover"
                    style={shapeClipPath ? { clipPath: shapeClipPath } : undefined}
                    sizes="(max-width: 1200px) 100vw, 1200px"
                  />
                </div>
              );
            } else {
              // Default (wrapped): Image fills entire canvas including bleed
              return convertedImageUrl.startsWith('data:') ? (
                <img
                  src={convertedImageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  style={shapeClipPath ? { clipPath: shapeClipPath } : undefined}
                />
              ) : (
                <Image
                  src={convertedImageUrl}
                  alt="Preview"
                  fill
                  className="object-cover"
                  style={shapeClipPath ? { clipPath: shapeClipPath } : undefined}
                  sizes="(max-width: 1200px) 100vw, 1200px"
                />
              );
            }
          })()}

          {/* Materiaalvoorbeeld (bijv. vurenhout) over het eindformaat, zoals in de editor. Alleen weergave. */}
          {!previewUnavailable && !isConverting && typeof finishingOptions.__materialOverlayUrl === 'string' && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={finishingOptions.__materialOverlayUrl}
              alt=""
              aria-hidden="true"
              data-material-overlay="true"
              className="absolute select-none"
              style={{
                top: `${bleedPercentage.top}%`,
                left: `${bleedPercentage.left}%`,
                width: `${100 - bleedPercentage.left - bleedPercentage.right}%`,
                height: `${100 - bleedPercentage.top - bleedPercentage.bottom}%`,
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          )}

          {/* Canvas Edge Overlays - Only in bleed zone (outside red line) */}
          {category?.toLowerCase().includes('canvas') && canvasEdgeType && canvasEdgeType !== 'wrapped' && (
            <>
              {/* White or Black edges - only in bleed area */}
              {(canvasEdgeType === 'white' || canvasEdgeType === 'black') && (
                <>
                  {/* Top edge */}
                  <div
                    className="absolute"
                    style={{
                      top: 0,
                      left: 0,
                      right: 0,
                      height: `${bleedPercentage.top}%`,
                      backgroundColor: canvasEdgeType === 'white' ? '#FFFFFF' : '#000000',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                  {/* Bottom edge */}
                  <div
                    className="absolute"
                    style={{
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `${bleedPercentage.bottom}%`,
                      backgroundColor: canvasEdgeType === 'white' ? '#FFFFFF' : '#000000',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                  {/* Left edge */}
                  <div
                    className="absolute"
                    style={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: `${bleedPercentage.left}%`,
                      backgroundColor: canvasEdgeType === 'white' ? '#FFFFFF' : '#000000',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                  {/* Right edge */}
                  <div
                    className="absolute"
                    style={{
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: `${bleedPercentage.right}%`,
                      backgroundColor: canvasEdgeType === 'white' ? '#FFFFFF' : '#000000',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                </>
              )}

              {/* Mirrored edges - mirror edge strips in bleed zones */}
              {canvasEdgeType === 'mirrored' && (
                <>
                  {/* Top mirrored edge */}
                  {mirroredEdges.top && (
                    <div
                      className="absolute overflow-hidden"
                      style={{
                        top: 0,
                        left: `${bleedPercentage.left}%`,
                        right: `${bleedPercentage.right}%`,
                        height: `${bleedPercentage.top}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <img
                        src={mirroredEdges.top}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center bottom',
                        }}
                      />
                    </div>
                  )}

                  {/* Bottom mirrored edge */}
                  {mirroredEdges.bottom && (
                    <div
                      className="absolute overflow-hidden"
                      style={{
                        bottom: 0,
                        left: `${bleedPercentage.left}%`,
                        right: `${bleedPercentage.right}%`,
                        height: `${bleedPercentage.bottom}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <img
                        src={mirroredEdges.bottom}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center top',
                        }}
                      />
                    </div>
                  )}

                  {/* Left mirrored edge */}
                  {mirroredEdges.left && (
                    <div
                      className="absolute overflow-hidden"
                      style={{
                        top: `${bleedPercentage.top}%`,
                        left: 0,
                        bottom: `${bleedPercentage.bottom}%`,
                        width: `${bleedPercentage.left}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <img
                        src={mirroredEdges.left}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'right center',
                        }}
                      />
                    </div>
                  )}

                  {/* Right mirrored edge */}
                  {mirroredEdges.right && (
                    <div
                      className="absolute overflow-hidden"
                      style={{
                        top: `${bleedPercentage.top}%`,
                        right: 0,
                        bottom: `${bleedPercentage.bottom}%`,
                        width: `${bleedPercentage.right}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    >
                      <img
                        src={mirroredEdges.right}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'left center',
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Stretched edges - stretch edge pixels into bleed zones */}
              {canvasEdgeType === 'stretched' && (
                <>
                  {/* Top stretched edge */}
                  {stretchedEdges.top && (
                    <div
                      className="absolute"
                      style={{
                        top: 0,
                        left: `${bleedPercentage.left}%`,
                        right: `${bleedPercentage.right}%`,
                        height: `${bleedPercentage.top}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                        backgroundImage: `url(${stretchedEdges.top})`,
                        backgroundSize: '100% 100%',
                      }}
                    />
                  )}

                  {/* Bottom stretched edge */}
                  {stretchedEdges.bottom && (
                    <div
                      className="absolute"
                      style={{
                        bottom: 0,
                        left: `${bleedPercentage.left}%`,
                        right: `${bleedPercentage.right}%`,
                        height: `${bleedPercentage.bottom}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                        backgroundImage: `url(${stretchedEdges.bottom})`,
                        backgroundSize: '100% 100%',
                      }}
                    />
                  )}

                  {/* Left stretched edge */}
                  {stretchedEdges.left && (
                    <div
                      className="absolute"
                      style={{
                        top: `${bleedPercentage.top}%`,
                        left: 0,
                        bottom: `${bleedPercentage.bottom}%`,
                        width: `${bleedPercentage.left}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                        backgroundImage: `url(${stretchedEdges.left})`,
                        backgroundSize: '100% 100%',
                      }}
                    />
                  )}

                  {/* Right stretched edge */}
                  {stretchedEdges.right && (
                    <div
                      className="absolute"
                      style={{
                        top: `${bleedPercentage.top}%`,
                        right: 0,
                        bottom: `${bleedPercentage.bottom}%`,
                        width: `${bleedPercentage.right}%`,
                        pointerEvents: 'none',
                        zIndex: 1,
                        backgroundImage: `url(${stretchedEdges.right})`,
                        backgroundSize: '100% 100%',
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* Uploaded Template Overlay (SVG lines only) */}
          {activeGuideTemplate && showGuides && (
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 11 }}>
              <img
                src={activeGuideTemplate}
                alt="Template Lines"
                className="w-full h-full"
                style={{
                  objectFit: 'fill',
                  mixBlendMode: 'normal',
                }}
              />
            </div>
          )}

          {/* Guides Overlay (show default guides if no template) */}
          {showGuides && !activeGuideTemplate && (
            <div className="absolute inset-0 pointer-events-none">
              {productShape !== 'rectangle' ? (
                <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {productShape === 'circle' ? (
                    <>
                      <ellipse cx="50" cy="50" rx={Math.max(0, 50 - shapeGuideInset)} ry={Math.max(0, 50 - shapeGuideInset)} fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="8 5" vectorEffect="non-scaling-stroke" />
                      {safetyMargin > 0 && (
                        <ellipse cx="50" cy="50" rx={Math.max(0, 50 - shapeSafeInset)} ry={Math.max(0, 50 - shapeSafeInset)} fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="8 5" vectorEffect="non-scaling-stroke" />
                      )}
                    </>
                  ) : (
                    <>
                      <polygon points={hexagonPoints(shapeGuideInset)} fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="8 5" vectorEffect="non-scaling-stroke" />
                      {safetyMargin > 0 && (
                        <polygon points={hexagonPoints(shapeSafeInset)} fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="8 5" vectorEffect="non-scaling-stroke" />
                      )}
                    </>
                  )}
                </svg>
              ) : (
                <>
                  {/* Bleed Zone (red) */}
                  <div
                    className="absolute border-2 border-red-500 border-dashed"
                    style={{
                      top: `${bleedPercentage.top}%`,
                      right: `${bleedPercentage.right}%`,
                      bottom: `${bleedPercentage.bottom}%`,
                      left: `${bleedPercentage.left}%`,
                      borderRadius: trimBorderRadius,
                    }}
                  />

                  {/* Safety Zone (green) - only if there is one */}
                  {safetyMargin > 0 && (
                    <div
                      className="absolute border-2 border-green-500 border-dashed"
                      style={{
                        top: `${safetyPercentage.top}%`,
                        right: `${safetyPercentage.right}%`,
                        bottom: `${safetyPercentage.bottom}%`,
                        left: `${safetyPercentage.left}%`,
                        borderRadius: trimBorderRadius,
                      }}
                    />
                  )}

                  {foldGuides.vertical.map((position, i) => (
                    <div
                      key={`fold-v-${i}`}
                      className="absolute w-0 border-l-2 border-dashed border-amber-500"
                      style={position}
                    />
                  ))}
                  {foldGuides.horizontal.map((position, i) => (
                    <div
                      key={`fold-h-${i}`}
                      className="absolute h-0 border-t-2 border-dashed border-amber-500"
                      style={position}
                    />
                  ))}
                  {tunnelGuides.map((position, i) => (
                    <div
                      key={`tunnel-${i}`}
                      className="absolute h-0 border-t-2 border-dashed border-sky-500"
                      style={position}
                    />
                  ))}
                </>
              )}
              {drillholePositions.map((position, i) => (
                <div
                  key={`drillhole-${i}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-black ring-1 ring-white"
                  style={{ ...position, ...drillholeSize }}
                />
              ))}
              {ringPositions.map((position, i) => (
                <div
                  key={`ring-${i}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-black ring-1 ring-white"
                  style={{ ...position, ...ringSize }}
                />
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Canvas Edge Type Info (if canvas product) */}
      {category?.toLowerCase().includes('canvas') && canvasEdgeType && canvasEdgeType !== 'wrapped' && (
        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-purple-900">Canvas zijkanten:</p>
              <div className="text-purple-800">
                {canvasEdgeType === 'white' && (
                  <div>
                    <strong>Witte zijkanten</strong>
                    <p className="text-xs">De zijkanten van je canvas worden wit.</p>
                  </div>
                )}
                {canvasEdgeType === 'black' && (
                  <div>
                    <strong>Zwarte zijkanten</strong>
                    <p className="text-xs">De zijkanten van je canvas worden zwart.</p>
                  </div>
                )}
                {canvasEdgeType === 'stretched' && (
                  <div>
                    <strong>Uitgetrokken zijkanten</strong>
                    <p className="text-xs">De rand pixels worden uitgerekt naar de zijkanten.</p>
                  </div>
                )}
                {canvasEdgeType === 'mirrored' && (
                  <div>
                    <strong>Gespiegelde zijkanten</strong>
                    <p className="text-xs">Je afbeelding wordt gespiegeld naar de zijkanten.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

{/* Low-res warning removed — Printen.eu handles file quality checks internally */}
    </div>
  );
}
